// The global printer registry — GET/POST/PUT/DELETE /printers. Registering
// a printer, and a PUT with rotateSecret: true, answer with the device
// secret exactly once; a rename answers with secret: null. The secret is
// never stored by the portal — it lives in this page's state until hidden.
// A printer a cloud print station still uses refuses deletion
// (printer_in_use).

import { useCallback, useEffect, useState } from 'react';
import { Plus, RotateCw } from 'lucide-react';
import { api } from '../../lib/api';
import type { Printer, PrinterInput, PrinterWithSecret } from './types';

export function Printers() {
  const [printers, setPrinters] = useState<Printer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState<{ printerId: string; name: string; secret: string } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState('');

  const load = useCallback(async () => {
    try {
      setPrinters(await api.get<Printer[]>('/printers'));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
      setError(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const show = (p: PrinterWithSecret) => { if (p.secret) setReveal({ printerId: p.printerId, name: p.name, secret: p.secret }); };

  const register = () => act(async () => {
    const body: PrinterInput = { name: name.trim() };
    show(await api.post<PrinterWithSecret>('/printers', body));
    setName('');
  });

  const rename = (id: string) => act(async () => {
    const body: PrinterInput = { name: renameTo.trim(), rotateSecret: false };
    await api.put<PrinterWithSecret>(`/printers/${id}`, body);
    setRenaming(null);
  });

  const rotate = (p: Printer) => {
    if (!confirm(`Rotate the secret for ${p.name}? The device must be re-paired with the new one.`)) return;
    void act(async () => {
      const body: PrinterInput = { name: p.name, rotateSecret: true };
      show(await api.put<PrinterWithSecret>(`/printers/${p.printerId}`, body));
    });
  };

  const remove = (p: Printer) => {
    if (!confirm(`Delete ${p.name} from the registry?`)) return;
    void act(async () => {
      await api.del(`/printers/${p.printerId}`);
      if (reveal?.printerId === p.printerId) setReveal(null);
    });
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Platform</div>
          <h1>Printer registry</h1>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      {reveal && (
        <div className="panel" style={{ marginBottom: 20, borderColor: 'var(--primary)' }} data-section="secret">
          <h2>Pairing secret · shown once</h2>
          <p style={{ fontSize: 13.5, margin: '8px 0' }}>
            <b>{reveal.name}</b> ({reveal.printerId}) — copy this secret into the device now.
            It is not stored and cannot be shown again; rotate it if it is lost.
          </p>
          <div className="docket" data-secret={reveal.printerId} style={{ fontSize: 16, userSelect: 'all', padding: '6px 0' }}>
            {reveal.secret}
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setReveal(null)}>Done — hide it</button>
        </div>
      )}

      <div className="panel" style={{ maxWidth: 560, marginBottom: 20 }} data-section="register">
        <h2>Register a printer</h2>
        <form style={{ display: 'flex', gap: 10, marginTop: 8 }}
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) void register(); }}>
          <input className="input" aria-label="Printer name" placeholder="Printer name" value={name}
            onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !name.trim()}>
            <Plus size={14} /> Register
          </button>
        </form>
      </div>

      {printers === null && !error && <div className="loading">Loading…</div>}
      {printers?.length === 0 && <div className="loading">No printers registered.</div>}
      {printers && printers.length > 0 && (
        <table className="tbl" data-section="printers">
          <thead>
            <tr>
              <th>Name</th>
              <th>Id</th>
              <th>Last seen</th>
              <th />
            </tr>
          </thead>
          <tbody style={{ cursor: 'default' }}>
            {printers.map((p) => (
              <tr key={p.printerId} data-printer={p.printerId} style={{ cursor: 'default' }}>
                <td style={{ fontWeight: 700 }} data-name>
                  {renaming === p.printerId ? (
                    <form style={{ display: 'inline-flex', gap: 8 }}
                      onSubmit={(e) => { e.preventDefault(); if (renameTo.trim()) void rename(p.printerId); }}>
                      <input className="input" aria-label={`New name for ${p.name}`} value={renameTo} style={{ width: 220 }}
                        onChange={(e) => setRenameTo(e.target.value)} />
                      <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !renameTo.trim()}>Save</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRenaming(null)}>Cancel</button>
                    </form>
                  ) : p.name}
                </td>
                <td className="docket" style={{ fontWeight: 500 }}>{p.printerId}</td>
                <td style={{ color: 'var(--ink-3)' }}>
                  {p.lastSeenAt
                    ? new Date(p.lastSeenAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
                    : 'never'}
                </td>
                <td className="num">
                  <span style={{ display: 'inline-flex', gap: 8 }}>
                    <button className="btn btn-outline btn-sm" disabled={busy} aria-label={`Rename ${p.name}`}
                      onClick={() => { setRenaming(p.printerId); setRenameTo(p.name); }}>
                      Rename
                    </button>
                    <button className="btn btn-outline btn-sm" disabled={busy} aria-label={`Rotate secret for ${p.name}`}
                      title="Invalidate the current secret and mint a new one" onClick={() => rotate(p)}>
                      <RotateCw size={13} /> Rotate secret
                    </button>
                    <button className="btn btn-danger btn-sm" disabled={busy} aria-label={`Delete ${p.name}`} onClick={() => remove(p)}>
                      Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
